<script>

export default {
  name: 'TwPaginator',
  components: {  },
  props: {
    modelValue: {
      type: Number,
      default: 1
    },
    total: {
      type: Number,
      default: 1
    },
    perPage: {
      type: Number,
      default: 20
    },
    loading: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'md'
    }
  },
  emits: ['update:modelValue'],
  computed: {
    sizeClasses() {
      return {
        sm: 'text-xs',
        md: 'text-sm',
        lg: 'text-lg'
      }[this.size]
    },
    pagesTotal() {
      return Math.ceil(this.total / this.perPage)
    },
    pages() {
      const totalPages = Math.ceil(this.total / this.perPage);

      let startPage = Math.max(this.modelValue - 3, 1); // Ensure it doesn't go below 1
      let endPage = Math.min(this.modelValue + 3, this.pagesTotal); // Ensure it doesn't go above this.pagesTotal

      // Adjust if start or end pages are too close to boundaries
      if (endPage - startPage < 6) {
        if (startPage === 1) {
          endPage = Math.min(startPage + 6, this.pagesTotal); // Adjust end page if at the beginning
        } else if (endPage === this.pagesTotal) {
          startPage = Math.max(endPage - 6, 1); // Adjust start page if at the end
        }
      }

      // Generate the list of page numbers to show
      const pages = [];
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      return pages;
    }
  }
}
</script>

<template>
  <nav class="flex items-center gap-x-1" aria-label="Pagination">
    <button :disabled="modelValue === 1" type="button" class="min-h-[32px] min-w-5 py-2 px-2.5 inline-flex justify-center items-center gap-x-1.5 text-sm rounded-lg text-gray-900 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none" aria-label="Previous" @click="$emit('update:modelValue', modelValue-1 )">
      <svg class="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="m15 18-6-6 6-6" />
      </svg>
    </button>
    <div class="flex items-center gap-x-1">
      <template v-if="modelValue > 4">
        <button type="button" :class="[modelValue === 1? 'pagination-button-active':'pagination-button-inactive']" @click="$emit('update:modelValue', 1)">
          1
        </button>
        <button type="button" class="pagination-button-inactive" disabled>
          ...
        </button>
      </template>
      <button v-for="page in pages" :key="'page-key-' + page" type="button" :class="modelValue === page? 'pagination-button-active':'pagination-button-inactive'" aria-current="page" @click="$emit('update:modelValue', page)">
        {{ page }}
      </button>
      <button v-if="modelValue < pagesTotal-4" type="button" class="pagination-button-inactive" disabled>
        ...
      </button>
      <button v-if="modelValue < pagesTotal-3" type="button" :class="[modelValue === pagesTotal? 'pagination-button-active':'pagination-button-inactive']" @click="$emit('update:modelValue', pagesTotal)">
        {{ pagesTotal }}
      </button>
    </div>
    <button :disabled="modelValue === pagesTotal" type="button" class="min-h-[32px] min-w-5 py-2 px-2.5 inline-flex justify-center items-center gap-x-1.5 text-sm rounded-lg text-gray-900 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none" aria-label="Next" @click="$emit('update:modelValue', modelValue+1)">
      <svg class="shrink-0 size-3.5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="m9 18 6-6-6-6" />
      </svg>
    </button>
  </nav>
</template>

<style scoped>
  .pagination-button {
    @apply relative -ml-px inline-flex items-center px-3 py-2 text-xs ring-1 text-xs ring-inset  focus:z-10;
  }

  .pagination-button-active {
    @apply min-h-[32px] min-w-5 flex justify-center items-center bg-gray-200 text-gray-900 py-1.5 px-3 text-sm rounded-lg focus:outline-none focus:bg-gray-300 disabled:opacity-50 disabled:pointer-events-none
  }

  .pagination-button-inactive {
    @apply min-h-[32px] min-w-5 flex justify-center items-center text-gray-900 hover:bg-gray-100 py-1.5 px-3 text-sm rounded-lg focus:outline-none focus:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none;
  }

</style>
